import { ds } from "js/core/models/dataService";
import { $ } from "js/vendor";
import firebase from "js/firebase";
import db from "js/db";
import { app } from "js/namespaces";
import { template } from "js/core/utilities/templates";
import { serverContext } from "js/config";
import Adapter from "js/core/storage/adapter";
import Profiler from "js/core/profiler";
import ConnectionToggleAdapter from "js/core/storage/connectionToggleAdapter";
import FirebaseAdapter from "js/core/storage/firebaseAdapter";
import { isElectron } from "js/config";
import { Presentation, PresentationPrivacyType } from "js/core/models/presentation";
import { PlayerPresenterView } from "js/player/playerPresenter";
import { PlayerView } from "js/player/playerView";
import { FallbackPlayerView } from "js/fallbackPlayer/fallbackPlayerView";
import { User } from "js/core/models/user";

import "css/player.scss";

//
// entry point
//

const adapter = isElectron ? ConnectionToggleAdapter : FirebaseAdapter;
Adapter.setDefaultClass(adapter, { readonly: true, autoSync: false });

window.isPlayer = true;

let player = {};

player.initialize = async function(el) {
    // Disable velocity hardware acceleration since it can cause images
    // to show up in the wrong place due to how it modifies css.
    $.Velocity.defaults.mobileHA = false;

    // options previously passed to player.initalize()
    player.options = serverContext.playerOptions;

    const profiler = Profiler.create({
        name: "player",
        type: Profiler.TIMER,
        warnTime: 2000
    });
    player.profiler = profiler;
    player.profilerId = profiler.start("initialize");

    const presentationId = serverContext.presentationId;
    const linkId = serverContext.linkId;
    const slideIndex = serverContext.slideIndex;

    // load the User model (if logged in)
    const currentUser = firebase.auth().currentUser;
    if (currentUser) {
        // sign into all Firebase app instances
        await db.updateCurrentUser(currentUser);
        // load user
        app.user = new User({ id: currentUser.uid }, { autoLoad: false });
        ds.dummySetup();
        await app.user.load();
    } else {
        ds.dummySetup();
    }

    // listen for sign out
    if (currentUser) {
        firebase.auth().onAuthStateChanged(user => {
            if (user == null) {
                window.location.reload();
            }
        });
    }

    let presentation = new Presentation({ id: presentationId });
    await presentation.load();

    // create the playerView here so we have the reference before the auth callback
    const isPresentationPublic =
        !presentation.get("secured") && presentation.get("public");
    let playerViewClass = player.options.isFallbackPlayer
        ? FallbackPlayerView
        : PlayerView;
    player.playerView = new playerViewClass({
        isEmbedded: window.self !== window.top,
        isDeckPage: player.options.isDeckPage,
        wasPlayedFromApp: false,
        isPublic: isPresentationPublic,
        isGetDeck: false,
        autoPlay: presentation.get("autoPlay"),
        autoPlayDuration: presentation.get("autoPlayDuration"),
        autoLoop: presentation.get("autoLoop"),
        showBranding: isElectron ? false : presentation.showBranding,
        allowPresenterNotes: true,
        showSocialSharing:
            presentation.get("showSocialSharing") &&
            presentation.getPrivacySetting() !== PresentationPrivacyType.SECURED
    });

    // optionally wrap with deck
    const $playerEl = player.playerView.render(player.options).$el;
    let $el;

    if (player.options.isDeckPage && !app.isMobileOrTablet) {
        const tpl = template("deckWrapper");
        const renderedHtml = tpl(serverContext);

        const $wrapped = $(renderedHtml);
        $wrapped.find(".deck-container").append($playerEl);
        $el = $wrapped;
    } else {
        $el = $playerEl;
    }

    $(el).append($el);

    player.playerView.linkId = linkId;

    await player.playerView.loadPresentationId(presentationId, slideIndex);
    player.profiler.end(player.profilerId);

    // deck ui sizes itself
    player.resizeDeck();
    $(window).on("resize", event => {
        player.resizeDeck();
    });
};

// originally from deck.js
player.resizeDeck = function() {
    let edgePadding = 30;

    let uiPadding = window.player.playerView.getPlayerPadding();

    let availableWidth = window.innerWidth - edgePadding * 2;
    let availableHeight =
        window.innerHeight -
        ($(".deck-info").outerHeight(true) || 0) -
        ($(".signup-footer").outerHeight(true) || 0);

    availableHeight -= 70;

    let playerWidth = availableWidth;
    let playerHeight = availableWidth * (720 / 1280) + uiPadding;

    if (playerHeight > availableHeight) {
        playerHeight = availableHeight;
        playerWidth = (playerHeight - uiPadding) * (1280 / 720);
    }

    let $container = $(".deck-container");

    $(".deck-info").width(playerWidth);
    $container.width(playerWidth).height(playerHeight);
};

player.initializePresenter = function(playerInstance) {
    let presenterView = new PlayerPresenterView({ player: playerInstance });
    $("body")
        .empty()
        .append(presenterView.render().$el);
    return presenterView;
};

export default player;

window.player = player;
